import QuestionTypeIcon from '../../../../components/Votings/QuestionTypeIcon';
import { questionSummary } from '../../../../lib/votings';
import { __, declension, formatDate } from '../../../../utils';
import { questionShape, type QuestionValues } from './formValues';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, IconButton, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

/** Čo je práve otvorené v pravom paneli. */
export type BuilderSelection = 'settings' | number;

type Props = {
  title: string;
  openFrom: Date | null;
  questions: QuestionValues[];
  selection: BuilderSelection;
  onSelect: (selection: BuilderSelection) => void;
  onAdd: () => void;
  onMove: (from: number, to: number) => void;
  variant: 'desktop' | 'mobile';
  disabled?: boolean;
};

const truncate = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
} as const;

/**
 * Ľavý panel buildera - celé hlasovanie na jednom mieste.
 *
 * Nahrádza zvislý stĺpec kariet, v ktorom sa pri troch otázkach s piatimi
 * možnosťami nedalo dostať k druhej otázke bez rolovania a nastavenia
 * hlasovania tlačili otázky dole. Odteraz sú nastavenia **prvou položkou
 * zoznamu**, nie kartou nad ním: je to jedna z vecí, medzi ktorými sa
 * prepína, nie hlavička obrazovky.
 *
 * Na mobile je z toho samostatná obrazovka (návrh 7a) a poradie sa mení
 * šípkami - preťahovanie prstom po zozname, ktorý sa zároveň roluje, je
 * nespoľahlivé.
 */
const VotingBuilderSidebar: React.FC<Props> = ({
  title,
  openFrom,
  questions,
  selection,
  onSelect,
  onAdd,
  onMove,
  variant,
  disabled = false,
}) => {
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const isMobile = variant === 'mobile';

  const meta = [
    questions.length
      ? `${questions.length} ${declension(questions.length, [
          __('otázka'),
          __('otázky'),
          __('otázok'),
        ])}`
      : __('Koncept · nepublikované'),
    openFrom ? `${__('otvorenie')} ${formatDate(openFrom)}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  const rows = questions.map((question, index) => {
    const active = selection === index;

    const content = (
      <>
        {!isMobile && (
          <Box
            component='span'
            aria-hidden
            sx={{
              width: 16,
              flex: 'none',
              fontSize: 13,
              letterSpacing: '-1px',
              color: active ? 'grey.400' : 'grey.300',
              cursor: disabled ? 'default' : 'grab',
            }}
          >
            ⠿
          </Box>
        )}

        <Box
          sx={({ palette, shape }) => ({
            width: isMobile ? 28 : 26,
            height: isMobile ? 28 : 26,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${shape.borderRadiusSm + 2}px`,
            backgroundColor: active
              ? palette.background.paper
              : palette.grey[100],
            border: active ? `1px solid ${palette.divider}` : 'none',
            color: active ? palette.primary.main : palette.grey[600],
          })}
        >
          <QuestionTypeIcon type={question.questionType} size={14} />
        </Box>

        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: isMobile ? 15 : 13,
              fontWeight: active ? 700 : 500,
              lineHeight: 1.35,
              ...truncate,
            }}
          >
            {`${index + 1} · ${question.title || __('Bez textu')}`}
          </Typography>
          <Typography
            sx={{ fontSize: isMobile ? 12 : 11, color: 'text.secondary' }}
          >
            {questionSummary(questionShape(question))}
          </Typography>
        </Stack>

        {isMobile && !disabled && (
          <Stack direction='row' gap={0.25} sx={{ flex: 'none' }}>
            <IconButton
              aria-label={__('Posunúť vyššie')}
              disabled={index === 0}
              onClick={(event) => {
                event.stopPropagation();
                onMove(index, index - 1);
              }}
              sx={{ width: 44, height: 44 }}
            >
              <KeyboardArrowUpIcon fontSize='small' />
            </IconButton>
            <IconButton
              aria-label={__('Posunúť nižšie')}
              disabled={index === questions.length - 1}
              onClick={(event) => {
                event.stopPropagation();
                onMove(index, index + 1);
              }}
              sx={{ width: 44, height: 44 }}
            >
              <KeyboardArrowDownIcon fontSize='small' />
            </IconButton>
          </Stack>
        )}
      </>
    );

    return (
      <Box
        key={index}
        role='button'
        tabIndex={0}
        onClick={() => onSelect(index)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(index);
          }
        }}
        draggable={!isMobile && !disabled}
        onDragStart={() => setDragIndex(index)}
        onDragEnd={() => setDragIndex(null)}
        onDragOver={(event: React.DragEvent) => {
          if (dragIndex === null || dragIndex === index) return;
          event.preventDefault();
        }}
        onDrop={(event: React.DragEvent) => {
          event.preventDefault();
          if (dragIndex === null || dragIndex === index) return;
          onMove(dragIndex, index);
          setDragIndex(null);
        }}
        sx={({ palette, shape }) => ({
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1.5 : 1,
          minHeight: isMobile ? 64 : 52,
          px: isMobile ? 1.75 : 1.25,
          py: isMobile ? 1.5 : 0,
          cursor: 'pointer',
          borderRadius: `${shape.borderRadius}px`,
          backgroundColor: active
            ? isMobile
              ? palette.grey[50]
              : palette.grey[100]
            : isMobile
              ? palette.background.paper
              : 'transparent',
          border: isMobile
            ? `1px solid ${active ? palette.grey[900] : palette.divider}`
            : '1px solid transparent',
          opacity: dragIndex === index ? 0.4 : 1,
          '&:hover': {
            backgroundColor: active
              ? isMobile
                ? palette.grey[50]
                : palette.grey[100]
              : palette.grey[50],
          },
        })}
      >
        {content}
      </Box>
    );
  });

  const list = (
    <Stack gap={isMobile ? 1 : 0.25}>
      <Box
        role='button'
        tabIndex={0}
        onClick={() => onSelect('settings')}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect('settings');
          }
        }}
        sx={({ palette, shape }) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minHeight: isMobile ? 48 : 38,
          px: 1.25,
          cursor: 'pointer',
          borderRadius: `${shape.borderRadius}px`,
          border: `1px solid ${
            selection === 'settings' ? 'transparent' : palette.divider
          }`,
          backgroundColor:
            selection === 'settings'
              ? palette.grey[100]
              : palette.background.paper,
          fontSize: isMobile ? 15 : 14,
          fontWeight: selection === 'settings' ? 700 : 500,
        })}
      >
        <SettingsOutlinedIcon
          sx={{
            fontSize: 18,
            color: selection === 'settings' ? 'primary.main' : 'grey.600',
          }}
        />
        <Box component='span' sx={{ flex: 1 }}>
          {__('Nastavenia hlasovania')}
        </Box>
      </Box>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ px: 1.25, pt: isMobile ? 1 : 1.75, pb: 0.75 }}
      >
        <Typography
          variant='overline'
          sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}
        >
          {__('Otázky')}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'grey.400' }}>
          {questions.length}
        </Typography>
      </Stack>

      {questions.length === 0 ? (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ px: 1.25, lineHeight: 1.5 }}
        >
          {isMobile
            ? __('Zatiaľ žiadne. Pridajte prvú otázku.')
            : __('Zatiaľ žiadne. Otázky sa objavia tu a dajú sa preťahovať.')}
        </Typography>
      ) : (
        rows
      )}

      {!disabled && (
        <Box
          component='button'
          type='button'
          onClick={onAdd}
          sx={({ palette, shape }) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            gap: 1,
            minHeight: isMobile ? 44 : 38,
            mt: 0.75,
            px: 1.25,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: isMobile ? 14 : 13,
            fontWeight: 700,
            color: palette.grey[600],
            border: `1px dashed ${palette.grey[300]}`,
            borderRadius: `${shape.borderRadius}px`,
            backgroundColor: 'transparent',
          })}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          {__('Pridať otázku')}
        </Box>
      )}
    </Stack>
  );

  if (isMobile) {
    return (
      <Stack gap={1.75} sx={{ p: 1.75 }}>
        <Stack gap={0.25}>
          <Typography
            component='b'
            sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}
          >
            {title || __('Bez názvu')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {meta}
          </Typography>
        </Stack>
        {list}
      </Stack>
    );
  }

  return (
    <Stack
      sx={({ palette }) => ({
        width: 304,
        flex: 'none',
        borderRight: `1px solid ${palette.divider}`,
        backgroundColor: palette.grey[50],
        minHeight: 0,
      })}
    >
      <Stack
        gap={0.25}
        sx={({ palette }) => ({
          px: 2,
          pt: 1.75,
          pb: 1.5,
          borderBottom: `1px solid ${palette.divider}`,
        })}
      >
        <Typography
          variant='overline'
          sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}
        >
          {__('Hlasovanie')}
        </Typography>
        <Typography
          component='b'
          sx={{
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3,
            color: title ? 'text.primary' : 'grey.400',
          }}
        >
          {title || __('Bez názvu')}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {meta}
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, py: 1.25 }}>
        {list}
      </Box>
    </Stack>
  );
};

export default VotingBuilderSidebar;
